import { Business } from "@mui/icons-material";
import { Box, Card, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import { RenderStates } from "~ui/atoms/RenderStates/RenderStates";
import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { TwoColumnGrid } from "~ui/molecules/TwoColumnGrid/TwoColumnGrid";
import { OrganizationItemWithAvatar } from "~ui/organisms/Organizations/OrganizationItemWithAvatar";
import { type Organization } from "~ui/organisms/Organizations/types";

import { NavLinkWrapper } from "../AdminOrganizationsTemplate/NavLinkWrapper";

export interface MyOrganizationsTemplateProps {
  data?: Organization[];
  status: Utils.QueryStatus;
  showHeader?: boolean;
  linkTo?: (organization: Organization) => string;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

function OrganizationsList({
  organizations,
  linkTo,
}: {
  organizations: Organization[];
  linkTo?: (organization: Organization) => string;
}) {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <TwoColumnGrid singleColumn={isMobileOrTablet}>
      {organizations.map((organization) => (
        <Card
          key={organization.id}
          sx={{
            "&:hover": {
              transform: "translateY(-2px)",
            },
            textDecoration: "none",
            transition: "transform 0.2s",
          }}
        >
          {linkTo ? (
            <NavLinkWrapper to={linkTo(organization)}>
              <OrganizationItemWithAvatar isSiteAdmin={false} organization={organization} />
            </NavLinkWrapper>
          ) : (
            <OrganizationItemWithAvatar isSiteAdmin={false} organization={organization} />
          )}
        </Card>
      ))}
    </TwoColumnGrid>
  );
}

export const MyOrganizationsTemplate = ({
  data: organizations,
  status,
  showHeader = true,
  linkTo,
  searchValue,
  onSearchChange,
}: MyOrganizationsTemplateProps) => {
  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden", p: { sm: 2, xs: 1 } }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        {showHeader && (
          <>
            <PageHeader title="My Organizations" icon={<Business data-variant="page-header" />} />
            <SearchField
              onChange={onSearchChange}
              placeholder="Search organizations..."
              value={searchValue}
              sx={{ mb: 1.5 }}
            />
          </>
        )}
        <RenderStates
          data={organizations}
          empty={<Typography>No organizations found</Typography>}
          error={<Typography color="error">Error loading organizations</Typography>}
          pending={<CircularProgress />}
          status={status}
          success={(orgs) => <OrganizationsList linkTo={linkTo} organizations={orgs} />}
        />
      </Stack>
    </Box>
  );
};
